/**
 * XPath strategy types shown in the Selectors tab (from `executionStrategies`).
 */
import { coalesceExecutionStrategies } from '../../../../../../features/automationStudioNew/executionStrategiesUtils';
export const ALLOWED_TYPES = [
    "xpath_custom",
    "xpath_custom_drag",
    "xpath_playwright",
    "xpath_target",
    "xpath_rules",
    "xpath_resilient",
    "xpath_positional",
    "xpath_dynamic",
];

export const SELECTOR_TYPE_MAP = {
    xpath_custom: { label: "Custom Selector", badgeTone: "neutral" },
    xpath_custom_drag: { label: "Custom Drag Selector", badgeTone: "neutral" },
    xpath_playwright: { label: "Targeted Selector", badgeTone: "primary" },
    xpath_target: { label: "Targeted Selector", badgeTone: "primary" },
    xpath_rules: { label: "Resilient Selector", badgeTone: "secondary" },
    xpath_resilient: { label: "Resilient Selector", badgeTone: "secondary" },
    xpath_positional: { label: "Positional Selector", badgeTone: "neutral" },
    xpath_dynamic: { label: "Dynamic Selector", badgeTone: "neutral" },
};

/** Replay: delete only xpath_custom / xpath_custom_drag (ConfigureXPath trash rule). */
export function isReplayDeletableType(type) {
    return type === "xpath_custom" || type === "xpath_custom_drag";
}

export function mapRowsToStepSelectors(rows) {
    return rows.map((r, i) => ({
        uid: r.uid,
        strategyId: r.strategyId,
        type: r.type,
        value: r.value,
        enabled: r.enabled !== false,
        priority: i,
    }));
}

export function mapSelectorTypeToStrategyType(type) {
    if (type && type.startsWith("xpath_")) return type;
    switch (type) {
        case "custom":
        default:
            return "xpath_custom";
    }
}

/**
 * Derives UI rows from `step.executionStrategies` (filtered by ALLOWED_TYPES).
 */
export function buildInitialSelectorsFromStep(step) {
    if (!step) return [];
    const raw = Array.isArray(step.executionStrategies) ? step.executionStrategies : [];
    const filtered = raw
        .filter((s) => s && ALLOWED_TYPES.includes(s.type) && s.type !== "playwright_code")
        .filter((s) => s.selector || s.xpath);

    return filtered
        .map((strategy, index) => ({
            uid: String(strategy.id ?? `${strategy.type}-${strategy.priority ?? index}`),
            strategyId: strategy.id,
            type: strategy.type || "xpath_custom",
            value: strategy.selector || strategy.xpath || "",
            enabled: strategy.enabled !== false,
            priority: strategy.priority !== undefined ? strategy.priority : index,
            isPending: false,
        }))
        .sort((a, b) => (a.priority || 0) - (b.priority || 0))
        .map((s, i) => ({ ...s, uid: s.uid || `${s.type}-${i}` }));
}

/** Human-readable xpath rows for generation / execution live views. */
export function getStepXPathSummary(step) {
    return buildInitialSelectorsFromStep(step).map((row) => ({
        label: SELECTOR_TYPE_MAP[row.type]?.label ?? row.type,
        value: row.value,
    }));
}

function getExecutedStrategyFromExecDetail(execDetail) {
    if (!execDetail) return null;
    return (
        execDetail?.result?.executedStrategy ??
        execDetail?.result?.result?.executedStrategy ??
        execDetail?.result?.executed_strategy ??
        execDetail?.result?.result?.executed_strategy ??
        null
    );
}

/** XPath used during replay (executed strategy) or planned selectors from the step. */
export function getStepExecutionXPathDisplay(step, execDetail = null) {
    const executed = getExecutedStrategyFromExecDetail(execDetail);
    if (executed) {
        const value = executed.selector ?? executed.xpath ?? executed.value;
        if (value) {
            return {
                label: SELECTOR_TYPE_MAP[executed.type]?.label ?? executed.type ?? 'Executed Selector',
                value,
                isExecuted: true,
            };
        }
    }

    if (!step) return null;

    const coalesced = coalesceExecutionStrategies(step, step);
    if (coalesced?.length > 0) {
        const primary = coalesced[0];
        return {
            label: SELECTOR_TYPE_MAP[primary.type]?.label ?? primary.type,
            value: primary.selector ?? primary.xpath ?? '',
            isExecuted: false,
        };
    }

    const summaries = getStepXPathSummary(step);
    if (summaries.length > 0) {
        return {
            label: summaries[0].label,
            value: summaries[0].value,
            isExecuted: false,
        };
    }

    return null;
}
